/**
 * PURPOSE: Seleccionar y subir el adjunto opcional (cero o uno) de un Comunicado.
 * RESPONSIBILITIES: Validar tipo/tamaño en cliente (espejo de los límites del backend), subir el
 *   archivo local a POST /attachments y devolver únicamente la metadata confiable que el backend
 *   generó — nunca permitir que el admin escriba/pegue una URL de adjunto a mano (FTR-0005 §13).
 * COLLABORATORS: HayllisCommunicationForm.jsx, hayllisNotificationsService.js.
 * ROUTE: winscore/src/admin/HayllisCommunications/HayllisCommunicationAttachmentField.jsx
 */
import React, { useRef, useState } from "react";
import { FiFile, FiImage, FiTrash2, FiUploadCloud } from "react-icons/fi";
import { toast } from "react-hot-toast";

import {
  getApiErrorMessage,
  uploadHayllisCommunicationAttachment,
} from "../../services/hayllisNotificationsService.js";
import { ATTACHMENT_TYPE_LABELS, formatBytes } from "./hayllisCommunicationsDisplay.utils.js";

// Client-side mirror of hayllisNotificationStorage.service.js — usability only, the backend
// re-validates independently and remains the real boundary.
const MAX_BYTES_BY_MIME = {
  "application/pdf": 10 * 1024 * 1024,
  "image/jpeg": 5 * 1024 * 1024,
  "image/png": 5 * 1024 * 1024,
  "image/webp": 5 * 1024 * 1024,
};
const ACCEPT_ATTR = "application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp";

const AttachmentIcon = ({ mime }) =>
  mime === "application/pdf" ? <FiFile className="shrink-0 text-red-500" /> : <FiImage className="shrink-0 text-blue-500" />;

const HayllisCommunicationAttachmentField = ({ value, onChange, disabled = false }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const openPicker = () => {
    if (disabled || uploading) return;
    if (inputRef.current) inputRef.current.value = "";
    inputRef.current?.click();
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const mime = String(file.type || "").toLowerCase();
    const maxBytes = MAX_BYTES_BY_MIME[mime];
    if (!maxBytes) {
      toast.error("Formato no permitido. Usa PDF, JPG, PNG o WEBP.");
      return;
    }
    if (file.size > maxBytes) {
      toast.error(
        mime === "application/pdf" ? "El PDF no puede superar 10 MB." : "La imagen no puede superar 5 MB."
      );
      return;
    }

    setUploading(true);
    try {
      const uploaded = await uploadHayllisCommunicationAttachment(file);
      onChange(uploaded);
      toast.success("Adjunto cargado.");
    } catch (error) {
      // One explicit user retry (re-click "Subir adjunto") is fine; no automatic retry, so a
      // failed attempt never silently creates more than one orphaned GCS object (FTR-0005 §14).
      toast.error(getApiErrorMessage(error, "No se pudo subir el adjunto."));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    if (disabled || uploading) return;
    onChange(null);
  };

  return (
    <div className="grid gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
      <span>Adjunto (opcional)</span>

      {value ? (
        // min-w-0 on this grid item is what lets the filename actually truncate: without it the
        // auto grid track grows to the row's min-content width and the actions spill out of the card.
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-slate-300 bg-slate-50 px-3 py-2.5 dark:border-slate-600 dark:bg-gray-800">
          <div className="flex min-w-0 flex-1 basis-48 items-center gap-2">
            <AttachmentIcon mime={value.mime} />
            <div className="min-w-0">
              <p className="truncate font-semibold text-slate-800 dark:text-white">{value.name}</p>
              <p className="text-xs font-normal text-slate-500 dark:text-slate-400">
                {ATTACHMENT_TYPE_LABELS[value.mime] || value.mime}
                {formatBytes(value.size) ? ` · ${formatBytes(value.size)}` : ""}
              </p>
            </div>
          </div>
          <div className="ml-auto flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={openPicker}
              disabled={disabled || uploading}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-bold text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-gray-700 dark:text-blue-300"
            >
              <FiUploadCloud />
              {uploading ? "Subiendo..." : "Reemplazar"}
            </button>
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled || uploading}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-bold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-gray-700"
            >
              <FiTrash2 />
              Quitar
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled || uploading}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-gray-800 dark:text-slate-200"
        >
          <FiUploadCloud />
          {uploading ? "Subiendo..." : "Subir adjunto"}
        </button>
      )}

      <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
        PDF (máx. 10 MB) o imagen JPG/PNG/WEBP (máx. 5 MB). Un solo adjunto.
      </span>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        onChange={handleFile}
        className="hidden"
        disabled={disabled || uploading}
      />
    </div>
  );
};

export default HayllisCommunicationAttachmentField;
